import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { useState } from 'react'

const menuButtonClass = (active: boolean) =>
  `px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
    active
      ? 'bg-[#EF8354] text-white'
      : 'text-[#4F5D75] dark:text-[#BFC0C0] hover:bg-[#BFC0C0]/20 dark:hover:bg-[#4F5D75]/20'
  }`

function MenuBar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null
  return (
    <div className="flex flex-wrap gap-0.5 border-b border-[#BFC0C0]/30 dark:border-[#4F5D75]/30 px-3 py-2">
      <button onClick={() => editor.chain().focus().toggleBold().run()} className={menuButtonClass(editor.isActive('bold'))}>B</button>
      <button onClick={() => editor.chain().focus().toggleItalic().run()} className={menuButtonClass(editor.isActive('italic'))}>I</button>
      <button onClick={() => editor.chain().focus().toggleStrike().run()} className={menuButtonClass(editor.isActive('strike'))}>S</button>
      <span className="mx-1 self-stretch border-r border-[#BFC0C0]/30 dark:border-[#4F5D75]/30" />
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={menuButtonClass(editor.isActive('heading', { level: 2 }))}>H2</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={menuButtonClass(editor.isActive('heading', { level: 3 }))}>H3</button>
      <span className="mx-1 self-stretch border-r border-[#BFC0C0]/30 dark:border-[#4F5D75]/30" />
      <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={menuButtonClass(editor.isActive('bulletList'))}>• List</button>
      <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={menuButtonClass(editor.isActive('orderedList'))}>1. List</button>
      <span className="mx-1 self-stretch border-r border-[#BFC0C0]/30 dark:border-[#4F5D75]/30" />
      <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={menuButtonClass(editor.isActive('blockquote'))}>❝</button>
    </div>
  )
}

export function RichTextEditor({
  initial,
  placeholder,
  onSave,
}: {
  initial?: string | null
  placeholder: string
  onSave: (html: string | null) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [savedHtml, setSavedHtml] = useState(initial ?? '')

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
    ],
    content: savedHtml || '',
    editable: editing,
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none outline-none px-4 py-3 min-h-[8rem]',
      },
    },
  })

  const startEditing = () => {
    setEditing(true)
    editor?.setEditable(true)
    setTimeout(() => editor?.commands.focus('end'), 0)
  }

  const save = async () => {
    const html = editor?.getHTML() ?? ''
    const value = html === '<p></p>' ? null : html
    setSavedHtml(value ?? '')
    setEditing(false)
    editor?.setEditable(false)
    await onSave(value)
  }

  const cancel = () => {
    editor?.commands.setContent(savedHtml || '')
    setEditing(false)
    editor?.setEditable(false)
  }

  return (
    <div className="bg-white dark:bg-[#2D3142] shadow-lg rounded-lg overflow-hidden">
      {editing && <MenuBar editor={editor} />}
      <div
        onClick={!editing ? startEditing : undefined}
        className={!editing ? 'cursor-text' : undefined}
      >
        {!editing && !savedHtml ? (
          <p className="px-4 py-3 text-sm text-[#BFC0C0] dark:text-[#4F5D75] italic select-none">
            {placeholder}
          </p>
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>
      {editing && (
        <div className="flex justify-end gap-2 border-t border-[#BFC0C0]/30 dark:border-[#4F5D75]/30 px-3 py-2">
          <button
            onClick={cancel}
            className="px-3 py-1 text-sm text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#2D3142] dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="px-3 py-1 text-sm font-medium rounded bg-[#EF8354] text-white hover:bg-[#EF8354]/80 transition-colors"
          >
            Save
          </button>
        </div>
      )}
    </div>
  )
}

export function FlightPost({ flightId, initial }: { flightId: number; initial?: string | null }) {
  return (
    <div className="mb-8">
      <RichTextEditor
        initial={initial}
        placeholder="Add a post about this flight…"
        onSave={post =>
          fetch(`/v0/flight/${flightId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ post }),
          }).then(() => {})
        }
      />
    </div>
  )
}
